import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, FileText, Download, Save, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import PrestationLibrary from './PrestationLibrary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteBuilder = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(null);

  const [quote, setQuote] = useState({
    title: '',
    object: '',
    description: '',
    valid_days: 30,
    acompte_percentage: 0,
    sections: [
      { title: 'Déplacements & Frais', category: 'travel_expenses', lines: [], order_index: 0 },
      { title: 'Main d\'œuvre', category: 'labor', lines: [], order_index: 1 },
      { title: 'Matériel', category: 'equipment', lines: [], order_index: 2 },
      { title: 'Divers', category: 'miscellaneous', lines: [], order_index: 3 }
    ]
  });

  const [totals, setTotals] = useState({
    total_ht: 0,
    total_tva: 0,
    total_ttc: 0,
    acompte_ttc: 0,
    solde_ttc: 0
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [quote.sections, quote.acompte_percentage]);

  const loadClients = async () => {
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/admin/clients/enhanced`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Erreur lors du chargement des clients');
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.client_id === clientId);
    setSelectedClient(client);
    setSelectedClientId(clientId);
  };

  const addLine = (sectionIndex) => {
    const newLine = {
      line_id: `line-${Date.now()}`,
      description: '',
      unit: 'unité',
      quantity: 1,
      unit_price_ht: 0,
      tva_rate: 20,
      total_ht: 0,
      tva_amount: 0,
      total_ttc: 0,
      order_index: quote.sections[sectionIndex].lines.length
    };

    const newSections = [...quote.sections];
    newSections[sectionIndex].lines.push(newLine);
    setQuote({ ...quote, sections: newSections });
  };

  const updateLine = (sectionIndex, lineIndex, field, value) => {
    const newSections = [...quote.sections];
    const line = newSections[sectionIndex].lines[lineIndex];
    
    line[field] = value;
    
    // Recalculate line totals
    const qty = parseFloat(line.quantity) || 0;
    const unitPrice = parseFloat(line.unit_price_ht) || 0;
    const tvaRate = parseFloat(line.tva_rate) || 0;
    
    line.total_ht = qty * unitPrice;
    line.tva_amount = line.total_ht * (tvaRate / 100);
    line.total_ttc = line.total_ht + line.tva_amount;
    
    setQuote({ ...quote, sections: newSections });
  };

  const removeLine = (sectionIndex, lineIndex) => {
    const newSections = [...quote.sections];
    newSections[sectionIndex].lines.splice(lineIndex, 1);
    setQuote({ ...quote, sections: newSections });
  };

  const addPrestationAsLine = (prestation) => {
    if (currentSectionIndex === null) return;
    
    const newLine = {
      line_id: `line-${Date.now()}`,
      presta_id: prestation.presta_id,
      description: prestation.name,
      unit: prestation.unit,
      quantity: prestation.default_quantity,
      unit_price_ht: prestation.unit_price_ht,
      tva_rate: 20,
      total_ht: prestation.default_quantity * prestation.unit_price_ht,
      tva_amount: (prestation.default_quantity * prestation.unit_price_ht) * 0.2,
      total_ttc: (prestation.default_quantity * prestation.unit_price_ht) * 1.2,
      order_index: quote.sections[currentSectionIndex].lines.length
    };
    
    const newSections = [...quote.sections];
    newSections[currentSectionIndex].lines.push(newLine);
    setQuote({ ...quote, sections: newSections });
    setShowLibrary(false);
    toast.success('Prestation ajoutée');
  };

  const calculateTotals = () => {
    let totalHT = 0;
    let totalTVA = 0;
    let totalTTC = 0;

    const newSections = quote.sections.map(section => {
      let sectionHT = 0;
      let sectionTVA = 0;
      let sectionTTC = 0;

      section.lines.forEach(line => {
        sectionHT += line.total_ht;
        sectionTVA += line.tva_amount;
        sectionTTC += line.total_ttc;
      });

      section.section_total_ht = sectionHT;
      section.section_tva = sectionTVA;
      section.section_total_ttc = sectionTTC;

      totalHT += sectionHT;
      totalTVA += sectionTVA;
      totalTTC += sectionTTC;

      return section;
    });

    const acomptePercentage = parseFloat(quote.acompte_percentage) || 0;
    const acompteTTC = (totalTTC * acomptePercentage) / 100;
    const soldeTTC = totalTTC - acompteTTC;

    setTotals({
      total_ht: totalHT,
      total_tva: totalTVA,
      total_ttc: totalTTC,
      acompte_ttc: acompteTTC,
      solde_ttc: soldeTTC
    });
  };

  const handleSaveQuote = async () => {
    if (!selectedClientId) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    if (!quote.title || !quote.object) {
      toast.error('Veuillez remplir le titre et l\'objet du devis');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('tkale_token');
      const payload = {
        client_id: selectedClientId,
        title: quote.title,
        object: quote.object,
        description: quote.description,
        valid_days: parseInt(quote.valid_days),
        sections: quote.sections.filter(s => s.lines.length > 0),
        acompte_percentage: parseFloat(quote.acompte_percentage) || 0
      };

      const response = await axios.post(`${API}/admin/quotes/create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Devis créé avec succès');
      
      // Download PDF
      if (response.data.quote && response.data.quote.quote_id) {
        await handleDownloadPDF(response.data.quote.quote_id);
      }
      
      // Redirect to quotes list
      setTimeout(() => {
        window.location.href = '/admin/quotes';
      }, 1500);
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error('Erreur lors de la création du devis');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (quoteId) => {
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/admin/quotes/${quoteId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `devis_${quoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF téléchargé');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erreur lors du téléchargement du PDF');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button onClick={() => window.location.href = '/dashboard'} variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-3xl font-bold text-slate-800">Créer un Devis</h1>
          </div>
          <Button onClick={handleSaveQuote} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Enregistrement...' : 'Enregistrer & Générer PDF'}
          </Button>
        </div>

        {/* Client Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Client</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sélectionner un client *</Label>
                <Select value={selectedClientId} onValueChange={handleClientSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map(client => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.name} - {client.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedClient && (
                <div className="border rounded p-3 bg-gray-50">
                  <p className="font-semibold">{selectedClient.name}</p>
                  <p className="text-sm text-gray-600">{selectedClient.email}</p>
                  <p className="text-sm text-gray-600">{selectedClient.phone}</p>
                  <p className="text-sm text-gray-600">{selectedClient.address}, {selectedClient.postal_code} {selectedClient.city}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quote Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Informations du Devis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Titre du devis *</Label>
                <Input 
                  value={quote.title}
                  onChange={(e) => setQuote({...quote, title: e.target.value})}
                  placeholder="Ex: Étude acoustique - Restaurant XYZ"
                />
              </div>
              <div>
                <Label>Objet *</Label>
                <Input 
                  value={quote.object}
                  onChange={(e) => setQuote({...quote, object: e.target.value})}
                  placeholder="Ex: Mesures acoustiques et rapport"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea 
                  value={quote.description}
                  onChange={(e) => setQuote({...quote, description: e.target.value})}
                  rows={3}
                />
              </div>
              <div>
                <Label>Validité (jours)</Label>
                <Input 
                  type="number"
                  value={quote.valid_days}
                  onChange={(e) => setQuote({...quote, valid_days: e.target.value})}
                />
              </div>
              <div>
                <Label>Acompte (%)</Label>
                <Input 
                  type="number"
                  step="0.1"
                  value={quote.acompte_percentage}
                  onChange={(e) => setQuote({...quote, acompte_percentage: e.target.value})}
                  placeholder="0"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sections */}
        {quote.sections.map((section, sectionIndex) => (
          <Card key={sectionIndex} className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{section.title}</CardTitle>
                <div className="flex space-x-2">
                  <Button 
                    size="sm" 
                    onClick={() => {
                      setCurrentSectionIndex(sectionIndex);
                      setShowLibrary(true);
                    }}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Bibliothèque
                  </Button>
                  <Button size="sm" onClick={() => addLine(sectionIndex)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter Ligne
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {section.lines.length === 0 ? (
                <p className="text-center text-gray-500 py-4">Aucune ligne</p>
              ) : (
                <div className="space-y-3">
                  {section.lines.map((line, lineIndex) => (
                    <div key={line.line_id} className="border rounded p-4 space-y-3">
                      <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-4">
                          <Label>Description</Label>
                          <Input 
                            value={line.description}
                            onChange={(e) => updateLine(sectionIndex, lineIndex, 'description', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Unité</Label>
                          <Select 
                            value={line.unit} 
                            onValueChange={(value) => updateLine(sectionIndex, lineIndex, 'unit', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unité">Unité</SelectItem>
                              <SelectItem value="heure">Heure</SelectItem>
                              <SelectItem value="jour">Jour</SelectItem>
                              <SelectItem value="km">Km</SelectItem>
                              <SelectItem value="forfait">Forfait</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1">
                          <Label>Qté</Label>
                          <Input 
                            type="number"
                            step="0.01"
                            value={line.quantity}
                            onChange={(e) => updateLine(sectionIndex, lineIndex, 'quantity', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>PU HT (€)</Label>
                          <Input 
                            type="number"
                            step="0.01"
                            value={line.unit_price_ht}
                            onChange={(e) => updateLine(sectionIndex, lineIndex, 'unit_price_ht', e.target.value)}
                          />
                        </div>
                        <div className="col-span-1">
                          <Label>TVA %</Label>
                          <Input 
                            type="number"
                            step="0.1"
                            value={line.tva_rate}
                            onChange={(e) => updateLine(sectionIndex, lineIndex, 'tva_rate', e.target.value)}
                          />
                        </div>
                        <div className="col-span-1">
                          <Label>Total TTC</Label>
                          <Input value={line.total_ttc.toFixed(2)} readOnly className="bg-gray-50" />
                        </div>
                        <div className="col-span-1 flex items-end">
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => removeLine(sectionIndex, lineIndex)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {/* Section Totals */}
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-end space-x-6 text-sm">
                      <div>Total HT: <span className="font-semibold">{section.section_total_ht?.toFixed(2) || '0.00'} €</span></div>
                      <div>TVA: <span className="font-semibold">{section.section_tva?.toFixed(2) || '0.00'} €</span></div>
                      <div>Total TTC: <span className="font-semibold">{section.section_total_ttc?.toFixed(2) || '0.00'} €</span></div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {/* Grand Totals */}
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex justify-between text-lg">
                <span>Total HT:</span>
                <span className="font-bold">{totals.total_ht.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-lg">
                <span>Total TVA:</span>
                <span className="font-bold">{totals.total_tva.toFixed(2)} €</span>
              </div>
              <div className="flex justify-between text-2xl border-t pt-3">
                <span className="font-bold">Total TTC:</span>
                <span className="font-bold text-blue-600">{totals.total_ttc.toFixed(2)} €</span>
              </div>
              
              {totals.acompte_ttc > 0 && (
                <>
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex justify-between">
                      <span>Acompte ({quote.acompte_percentage}%):</span>
                      <span className="font-semibold text-green-600">{totals.acompte_ttc.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Solde:</span>
                      <span className="font-semibold">{totals.solde_ttc.toFixed(2)} €</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Prestation Library Modal */}
      {showLibrary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">
                  Bibliothèque de Prestations - {quote.sections[currentSectionIndex]?.title}
                </h2>
                <Button onClick={() => setShowLibrary(false)} variant="ghost">
                  ✕
                </Button>
              </div>
              <PrestationLibrary 
                onSelectPrestation={addPrestationAsLine} 
                filterByCategory={quote.sections[currentSectionIndex]?.category}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteBuilder;
